import './case-study.scss';

import React from 'react';

import { formatEntryDate, getEntryAuthor } from '../../../content';
import type {
  BaseEntry,
  CaseStudyApproachItem,
  CaseStudyCallout,
  CaseStudyFieldItem,
  CaseStudyImpactItem,
  CaseStudyInfographicSection,
  CaseStudyLearningItem,
  CaseStudyOutcomeTier,
  CaseStudyStackItem,
  CaseStudyStatStripItem,
} from '../../../content/types/entry';
import { Breadcrumbs } from '../../layout';
import { PersonByline } from '../../sections/people/PersonByline';

type CaseStudyProps = {
  entry: BaseEntry & { client?: string };
  parent: { name: string; path: string };
};

const InfographicEyebrow: React.FC<{ children: string }> = ({ children }) => (
  <p className="case-study-infographic__eyebrow mb-0">{children}</p>
);

const FieldsInfographic: React.FC<{
  section: CaseStudyInfographicSection<CaseStudyFieldItem>;
}> = ({ section }) => (
  <div className="case-study-infographic case-study-infographic--inline mt-5">
    {section.eyebrow ? <InfographicEyebrow>{section.eyebrow}</InfographicEyebrow> : null}
    <h3 className="case-study-infographic__title">{section.title}</h3>
    {section.description ? (
      <p className="case-study-infographic__description">{section.description}</p>
    ) : null}
    <div className="case-study-infographic__field-grid">
      {section.items.map((field) => (
        <div key={field.label} className="case-study-infographic__field-pill">
          <i className={`bx ${field.icon}`} aria-hidden="true"></i>
          {field.label}
        </div>
      ))}
    </div>
  </div>
);

const StackInfographic: React.FC<{
  section: CaseStudyInfographicSection<CaseStudyStackItem>;
}> = ({ section }) => (
  <div className="case-study-infographic case-study-infographic--inline mt-5">
    {section.eyebrow ? <InfographicEyebrow>{section.eyebrow}</InfographicEyebrow> : null}
    <h3 className="case-study-infographic__title">{section.title}</h3>
    {section.description ? (
      <p className="case-study-infographic__description">{section.description}</p>
    ) : null}
    <div className="case-study-infographic__stack-row">
      {section.items.map((item) => (
        <div key={item.label} className="case-study-infographic__stack-card">
          <div className="case-study-infographic__stack-label">{item.label}</div>
          <p className="case-study-infographic__stack-value">{item.value}</p>
        </div>
      ))}
    </div>
  </div>
);

const StatStrip: React.FC<{ items: CaseStudyStatStripItem[] }> = ({ items }) => (
  <div className="case-study-infographic__stat-strip case-study-infographic">
    {items.map((stat) => (
      <div key={stat.label} className="case-study-infographic__stat-card">
        <div className="case-study-infographic__stat-num">{stat.value}</div>
        <p className="case-study-infographic__stat-label">{stat.label}</p>
      </div>
    ))}
  </div>
);

const ApproachInfographic: React.FC<{
  section: CaseStudyInfographicSection<CaseStudyApproachItem>;
}> = ({ section }) => (
  <div className="case-study-infographic case-study-infographic--inline mt-5">
    {section.eyebrow ? <InfographicEyebrow>{section.eyebrow}</InfographicEyebrow> : null}
    <h3 className="case-study-infographic__title">{section.title}</h3>
    {section.description ? (
      <p className="case-study-infographic__description">{section.description}</p>
    ) : null}
    <div className="case-study-infographic__approach-steps">
      {section.items.map((step) => (
        <div key={step.title} className="case-study-infographic__step-block">
          <div className="case-study-infographic__step-icon">
            {step.step != null ? (
              <span className="case-study-infographic__step-num">{step.step}</span>
            ) : (
              <i className={`bx ${step.icon}`} aria-hidden="true"></i>
            )}
          </div>
          <div>
            <p className="case-study-infographic__step-title">{step.title}</p>
            <p className="case-study-infographic__step-desc">{step.description}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ImpactsInfographic: React.FC<{
  section: CaseStudyInfographicSection<CaseStudyImpactItem>;
}> = ({ section }) => (
  <div className="case-study-infographic case-study-infographic--inline mt-5">
    {section.eyebrow ? <InfographicEyebrow>{section.eyebrow}</InfographicEyebrow> : null}
    <h3 className="case-study-infographic__title">{section.title}</h3>
    {section.description ? (
      <p className="case-study-infographic__description">{section.description}</p>
    ) : null}
    <div className="case-study-infographic__impact-row">
      {section.items.map((impact) => (
        <div key={impact.title} className="case-study-infographic__impact-card">
          <div className="case-study-infographic__impact-icon">
            <i className={`bx ${impact.icon}`} aria-hidden="true"></i>
          </div>
          <p className="case-study-infographic__impact-title">{impact.title}</p>
          <p className="case-study-infographic__impact-desc">{impact.description}</p>
        </div>
      ))}
    </div>
  </div>
);

const OutcomesInfographic: React.FC<{
  section: CaseStudyInfographicSection<CaseStudyOutcomeTier>;
}> = ({ section }) => (
  <div className="case-study-infographic case-study-infographic--inline mt-5">
    {section.eyebrow ? <InfographicEyebrow>{section.eyebrow}</InfographicEyebrow> : null}
    <h3 className="case-study-infographic__title">{section.title}</h3>
    {section.description ? (
      <p className="case-study-infographic__description">{section.description}</p>
    ) : null}
    <div className="case-study-infographic__outcomes-grid">
      {section.items.map((tier) => (
        <div key={tier.label} className="case-study-infographic__outcome-tier">
          <div className="case-study-infographic__outcome-label">{tier.label}</div>
          <ul className="case-study-infographic__outcome-list">
            {tier.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

const LearningsInfographic: React.FC<{
  section: CaseStudyInfographicSection<CaseStudyLearningItem>;
}> = ({ section }) => (
  <div className="case-study-infographic case-study-infographic--inline mt-5">
    {section.eyebrow ? <InfographicEyebrow>{section.eyebrow}</InfographicEyebrow> : null}
    <h3 className="case-study-infographic__title">{section.title}</h3>
    {section.description ? (
      <p className="case-study-infographic__description">{section.description}</p>
    ) : null}
    <ul className="case-study-infographic__learnings-list">
      {section.items.map((item) => (
        <li key={item.text} className="case-study-infographic__learning-item">
          <i className="bx bx-check-circle" aria-hidden="true"></i>
          <span>{item.text}</span>
        </li>
      ))}
    </ul>
  </div>
);

const CalloutBanner: React.FC<{ callout: CaseStudyCallout }> = ({ callout }) => (
  <div className="case-study-infographic case-study-infographic--inline mt-4">
    <div className="case-study-infographic__callout">
      <div className="case-study-infographic__callout-icon">
        <i className={`bx ${callout.icon}`} aria-hidden="true"></i>
      </div>
      <div>
        <p className="case-study-infographic__callout-title">{callout.title}</p>
        <p className="case-study-infographic__callout-desc">{callout.description}</p>
      </div>
    </div>
  </div>
);

export const CaseStudy: React.FC<CaseStudyProps> = ({ entry, parent }) => {
  const author = getEntryAuthor(entry);
  const { caseStudy } = entry;

  const breadcrumbs = [
    { name: parent.name, isActive: false, path: parent.path },
    { name: entry.title, isActive: true },
  ];

  return (
    <div className="container">
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <section className="pb-4 mb-2 mb-lg-3">
        <h1>{entry.title}</h1>
        <div className="d-flex flex-wrap gap-2 mb-0">
          {entry.tags.map((tag) => (
            <span key={tag} className="badge text-nav bg-secondary text-decoration-none entry-tag">
              {tag}
            </span>
          ))}
        </div>
        {entry.client ? <p className="mb-0 mt-1">Client: {entry.client}</p> : null}
      </section>

      <section className="case-study-hero">
        <img className="case-study-hero__image" src={entry.image} alt={entry.title} />
      </section>

      <section className="py-5 my-1 my-md-4 my-lg-5">
        <div className="row">
          <div className="col-lg-7 mb-4 mb-lg-0">
            <div className="pe-lg-4 me-lg-3 pe-xl-0 me-xl-0">
              <h2 className="h1 mb-4">About</h2>
              <p className="mb-0">{caseStudy.about}</p>
              {caseStudy.infographics?.statStrip?.length ? (
                <StatStrip items={caseStudy.infographics.statStrip} />
              ) : null}
              {caseStudy.infographics?.fields ? (
                <FieldsInfographic section={caseStudy.infographics.fields} />
              ) : null}
              {caseStudy.infographics?.stack ? (
                <StackInfographic section={caseStudy.infographics.stack} />
              ) : null}
              {caseStudy.infographics?.approach ? (
                <ApproachInfographic section={caseStudy.infographics.approach} />
              ) : null}
              {caseStudy.infographics?.impacts ? (
                <ImpactsInfographic section={caseStudy.infographics.impacts} />
              ) : null}
              {caseStudy.infographics?.outcomes ? (
                <OutcomesInfographic section={caseStudy.infographics.outcomes} />
              ) : null}
              {caseStudy.infographics?.learnings ? (
                <LearningsInfographic section={caseStudy.infographics.learnings} />
              ) : null}
              {caseStudy.infographics?.callout ? (
                <CalloutBanner callout={caseStudy.infographics.callout} />
              ) : null}
            </div>
          </div>
          <div className="col-lg-5 col-xl-4 offset-xl-1 border-start-lg">
            <div className="ps-lg-4 ms-lg-3">
              <h3 className="h5 d-flex align-items-center">
                <i className="bx bx-help-circle text-primary fs-4 me-2"></i>
                Challenge
              </h3>
              <p className="pb-4 mb-0 mb-lg-3">{caseStudy.challenge}</p>
              <h3 className="h5 d-flex align-items-center">
                <i className="bx bx-bulb text-primary fs-4 me-2"></i>
                Solution
              </h3>
              <p className="mb-0">{caseStudy.solution}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 my-2 my-md-4 my-lg-5">
        <div className="row py-md-3">
          <div className="col-md-3 offset-lg-1">
            <h2>Results</h2>
          </div>
          <div className="col-lg-7 col-md-9">
            <p className="pb-4 mb-2 mb-lg-3">{caseStudy.results}</p>
            {caseStudy.stats?.length ? (
              <div className="row row-cols-1 row-cols-sm-3 g-4">
                {caseStudy.stats.map((stat) => (
                  <div key={stat.label} className="col">
                    <h3 className="h1 mb-2">{stat.value}</h3>
                    <p className="mb-0">
                      <span className="fw-semibold">{stat.label}</span>
                      {stat.context ? ` ${stat.context}` : null}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {caseStudy.testimonial ? (
        <section className="bg-secondary">
          <div className="py-2 py-md-4 py-lg-5">
            <div className="row">
              {caseStudy.testimonial.backgroundImage ? (
                <div className="col-md-4">
                  <div className="position-relative d-flex h-100">
                    <div
                      className="position-absolute top-0 start-0 w-100 h-100 rounded-3 bg-size-cover bg-repeat-0 bg-position-center"
                      style={{ backgroundImage: `url(${caseStudy.testimonial.backgroundImage})` }}
                    ></div>
                  </div>
                </div>
              ) : null}
              <div className={caseStudy.testimonial.backgroundImage ? 'col-md-6' : 'col-12'}>
                <figure className="card position-relative border-0 shadow-sm py-3 p-0 p-lg-4 mt-4 mb-0 ms-xl-5">
                  <span className="btn btn-icon btn-primary btn-lg shadow-primary pe-none position-absolute top-0 start-0 translate-middle-y ms-4 ms-lg-5">
                    <i className="bx bxs-quote-left"></i>
                  </span>
                  <blockquote className="card-body mt-2 mb-0">
                    <p>{caseStudy.testimonial.quote}</p>
                  </blockquote>
                  <figcaption className="card-footer border-0 d-sm-flex pt-0 mt-n3 mt-lg-0">
                    <div className="d-flex align-items-center border-end-sm pe-sm-4 me-sm-4">
                      {caseStudy.testimonial.authorImage ? (
                        <img
                          src={caseStudy.testimonial.authorImage}
                          width="48"
                          className="d-md-none rounded-circle"
                          alt={caseStudy.testimonial.authorName}
                        />
                      ) : null}
                      <div className="ps-3 ps-md-0">
                        <h5 className="fw-semibold lh-base mb-0">{caseStudy.testimonial.authorName}</h5>
                        <span className="fs-sm text-muted person-role">{caseStudy.testimonial.authorRole}</span>
                      </div>
                    </div>
                    {caseStudy.testimonial.companyLogo ? (
                      <img
                        src={caseStudy.testimonial.companyLogo}
                        width="160"
                        className="d-block mt-2 ms-5 mt-sm-0 ms-sm-0"
                        alt="Company logo"
                      />
                    ) : null}
                  </figcaption>
                </figure>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <section className="py-5">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          {author ? (
            <PersonByline name={author.name} role={author.role} image={author.avatar ?? author.image} />
          ) : null}
          <span className="fs-sm text-muted entry-date">{formatEntryDate(entry.date)}</span>
        </div>
        {entry.link ? (
          <div className="mt-4">
            <a href={entry.link} target="_blank" rel="noreferrer" className="btn btn-outline-primary">
              Visit website
            </a>
          </div>
        ) : null}
      </section>
    </div>
  );
};
